import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import { useWorkspaceStore } from '../stores/workspace.js';
import type { User, Workspace } from '@mayksel/types';

export default function JoinWorkspace() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { user: clerkUser } = useUser();
  const setWorkspaceData = useWorkspaceStore((s) => s.setWorkspaceData);

  const [status, setStatus] = useState<'loading' | 'error' | 'done'>('loading');
  const [message, setMessage] = useState('Accepting invite…');

  const inviteId = searchParams.get('invite');

  useEffect(() => {
    if (!inviteId || !clerkUser) return;

    void (async () => {
      try {
        const token = await getToken();
        const baseUrl = import.meta.env.VITE_API_URL ?? '';

        const res = await fetch(`${baseUrl}/v1/members/accept-invite`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            inviteId,
            clerkUserId: clerkUser.id,
            name: clerkUser.fullName ?? clerkUser.primaryEmailAddress?.emailAddress ?? 'User',
            email: clerkUser.primaryEmailAddress?.emailAddress ?? '',
            avatarUrl: clerkUser.imageUrl ?? null,
          }),
        });

        if (!res.ok) {
          const body = await res.json().catch(() => ({})) as { error?: { message?: string } };
          setMessage(body.error?.message ?? 'This invite is invalid or has expired.');
          setStatus('error');
          return;
        }

        const body = await res.json() as { data: { workspace: Workspace; user: User } };
        setWorkspaceData(body.data.workspace, body.data.user);
        setStatus('done');
        navigate('/dashboard', { replace: true });
      } catch {
        setMessage('Something went wrong. Please try again.');
        setStatus('error');
      }
    })();
  }, [inviteId, clerkUser?.id]);

  if (!inviteId) {
    return (
      <div style={centerStyle}>
        <p style={{ color: 'var(--color-danger)', fontSize: 'var(--text-sm)' }}>Invalid invite link.</p>
      </div>
    );
  }

  return (
    <div style={centerStyle}>
      {status === 'loading' && (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>{message}</p>
      )}
      {status === 'error' && (
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: 'var(--color-danger)', fontSize: 'var(--text-sm)', marginBottom: 16 }}>{message}</p>
          <button
            onClick={() => navigate('/')}
            style={{ padding: '8px 16px', background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}
          >
            Go home
          </button>
        </div>
      )}
    </div>
  );
}

const centerStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
